import { useState } from 'react';
import { Timer } from 'lucide-react';
import { useStepPerformance, useOrganisationPerformance } from '../hooks/useApi';
import { DataTable } from '../components/shared/DataTable';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import type { StepPerformance, OrganisationPerformance } from '../types/api';

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}min`;
}

function formatSeconds(s: number): string {
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}min ${s % 60}s`;
}

// Time-range options expressed in minutes so 15 min / 1h sit alongside the
// longer windows in a single dropdown (the API takes a `minutes` param).
const STEP_RANGES = [
  { minutes: 15, label: 'Ostatnie 15 min' },
  { minutes: 60, label: 'Ostatnia godzina' },
  { minutes: 360, label: 'Ostatnie 6h' },
  { minutes: 1440, label: 'Ostatnie 24h' },
  { minutes: 4320, label: 'Ostatnie 72h' },
];

const ORG_RANGES = [
  { minutes: 15, label: 'Ostatnie 15 min' },
  { minutes: 60, label: 'Ostatnia godzina' },
  { minutes: 1440, label: 'Ostatni dzien' },
  { minutes: 10080, label: 'Ostatnie 7 dni' },
  { minutes: 43200, label: 'Ostatnie 30 dni' },
];

export function PerformancePage() {
  const [stepMinutes, setStepMinutes] = useState(1440);
  const [orgMinutes, setOrgMinutes] = useState(10080);

  const steps = useStepPerformance(stepMinutes);
  const orgs = useOrganisationPerformance(orgMinutes);

  const stepColumns = [
    { key: 'step', header: 'Krok', sortable: true },
    { key: 'count', header: 'Ilosc', sortable: true },
    {
      key: 'avgMs',
      header: 'Sredni czas',
      sortable: true,
      render: (row: StepPerformance) => formatMs(row.avgMs),
    },
    {
      key: 'p95Ms',
      header: 'P95',
      sortable: true,
      render: (row: StepPerformance) => (
        <span className={row.p95Ms > 10000 ? 'text-amber-600 dark:text-amber-300' : ''}>{formatMs(row.p95Ms)}</span>
      ),
    },
    {
      key: 'minMs',
      header: 'Min',
      render: (row: StepPerformance) => formatMs(row.minMs),
    },
    {
      key: 'maxMs',
      header: 'Max',
      sortable: true,
      render: (row: StepPerformance) => (
        <span className={row.maxMs > 30000 ? 'text-red-600 dark:text-red-300' : ''}>{formatMs(row.maxMs)}</span>
      ),
    },
  ];

  const orgColumns = [
    { key: 'organisationName', header: 'Organizacja', sortable: true },
    { key: 'credentialCount', header: 'Ilosc', sortable: true },
    {
      key: 'avgTotalSeconds',
      header: 'Sredni czas calkowity',
      sortable: true,
      render: (row: OrganisationPerformance) => formatSeconds(row.avgTotalSeconds),
    },
    {
      key: 'avgPdfFillingMs',
      header: 'PDF',
      render: (row: OrganisationPerformance) => formatMs(row.avgPdfFillingMs),
    },
    {
      key: 'avgSigningMs',
      header: 'Podpisywanie',
      render: (row: OrganisationPerformance) => formatMs(row.avgSigningMs),
    },
    {
      key: 'avgSendingMs',
      header: 'Wysylka',
      render: (row: OrganisationPerformance) => formatMs(row.avgSendingMs),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Timer className="h-6 w-6 text-brand-600 dark:text-brand-400" />
          Wydajnosc
        </h1>
        <p className="text-sm text-gray-500 mt-1">Czasy przetwarzania krokow pipeline</p>
      </div>

      {/* Step Performance */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Czasy per krok</h2>
          <select value={stepMinutes} onChange={(e) => setStepMinutes(Number(e.target.value))} className="input-field text-sm">
            {STEP_RANGES.map((r) => (
              <option key={r.minutes} value={r.minutes}>{r.label}</option>
            ))}
          </select>
        </div>
        {steps.isLoading ? (
          <LoadingSpinner size="sm" />
        ) : steps.error ? (
          <ErrorAlert message={steps.error.message} onRetry={() => steps.refetch()} />
        ) : steps.data ? (
          <DataTable columns={stepColumns} data={steps.data} compact />
        ) : null}
      </div>

      {/* Organisation Performance */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Wydajnosc per organizacja</h2>
          <select value={orgMinutes} onChange={(e) => setOrgMinutes(Number(e.target.value))} className="input-field text-sm">
            {ORG_RANGES.map((r) => (
              <option key={r.minutes} value={r.minutes}>{r.label}</option>
            ))}
          </select>
        </div>
        {orgs.isLoading ? (
          <LoadingSpinner size="sm" />
        ) : orgs.error ? (
          <ErrorAlert message={orgs.error.message} onRetry={() => orgs.refetch()} />
        ) : orgs.data ? (
          <DataTable columns={orgColumns} data={orgs.data} compact />
        ) : null}
      </div>
    </div>
  );
}
